import { useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { ApiGet, ApiDelete } from "../helper/axiosFunctions";
import { commonToast } from "../helper/Toast";
import CreateDeckModel from "../components/model/CreateDeckModel";
import CreateRegularDeckModel from "../components/model/CreateRegularDeckModel";
import { removeAlertPopup } from "../helper/commonFunctions";
import { setTabData } from "../redux/slices/tabSlice";
import DeckCard, { DeckCardSkeleton } from "../components/DeckCard";
import Button from "../components/ui/Button";

const STATUS_TABS = ["ALL", "PUBLISHED", "HIDDEN", "PRIVATE", "WORK_IN_PROGRESS"];
const HIDDEN_STATUSES = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED"];

const isVisibleDeck = (deck) => {
  const status = deck?.status?.toUpperCase();
  return !HIDDEN_STATUSES.includes(status);
};

export default function WorkshopPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [editingDeck, setEditingDeck] = useState(null);
  const user_data = useSelector((state) => state.profileData.profileData);
  const [deckData, setDeckData] = useState([]);
  const [activeTab, setActiveTab] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [addRegularDeckModel, setAddRegularDeckModel] = useState(false);

  const handleDelete = (deck) => {
    removeAlertPopup({
      title: t("areYouSureYouWantToDeleteDeck"),
      t: t,
      preConfirm: async () => {
        try {
          await ApiDelete(`/contents/decks/${deck.deckId}`);
          commonToast(t("deckDeletedSuccess"), "success");
          fetchData();
        } catch (error) {
          commonToast(
            error?.data?.errorDescription || error?.data?.error || error?.message,
            "error"
          );
        }
      },
    });
  };

  const handleEdit = (deck) => {
    setEditingDeck(deck);
    setAddRegularDeckModel(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingDeck(null);
  };

  const fetchData = async () => {
    if (!user_data?.userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await ApiGet("/contents/decks/authors/" + user_data.userId);
      const allDecks = res?.data || [];
      setDeckData(allDecks.filter(isVisibleDeck));
    } catch (error) {
      commonToast(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user_data?.userId]);

  useEffect(() => {
    dispatch(setTabData("workshops"));
  }, [dispatch]);

  const filteredDecks = useMemo(() => {
    return deckData.filter(
      (item) =>
        activeTab === "ALL" ||
        String(item?.status || "").toUpperCase() === String(activeTab).toUpperCase()
    );
  }, [deckData, activeTab]);

  return (
    <div className="p-10 md:p-5">
      <div className="flex md:flex-col md:gap-3 justify-between items-center">
        <h1 className="text-2xl font-semibold">{t("deckworkshop")}</h1>
        <div className="flex items-center gap-3">
          <h2 className="regular-text ">{t("creatdeck")}</h2>
          <FiPlus
            fontSize={20}
            onClick={() => setOpen(true)}
            className="border cursor-pointer rounded-full size-8 p-1 flex justify-center items-center"
          />
        </div>
      </div>

      <div className="flex border-t pt-5 mt-5 flex-wrap justify-between items-center my-3">
        <div className="flex w-fit max-w-full items-center gap-1.5 overflow-auto rounded-full border border-gray-200 bg-gray-50 p-1.5 md:mt-3">
          {STATUS_TABS.map((item) => (
            <button
              key={item}
              type="button"
              className={`shrink-0 rounded-full px-4 py-2 text-[15px] transition-colors ${
                item === activeTab
                  ? "bg-darkblue font-semibold text-white shadow-sm"
                  : "text-gray-600 hover:bg-white"
              }`}
              onClick={() => setActiveTab(item)}
            >
              {t(item)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-5 grid-cols-4 xl:grid-cols-3 md:!grid-cols-1 mt-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <DeckCardSkeleton key={i} />
          ))}
        </div>
      ) : filteredDecks.length === 0 ? (
        <div className="mt-12 text-center py-16">
          <p className="text-lg text-gray-400 mb-4">
            {t("noDecksFound") || "No decks found"}
          </p>
          <Button variant="primary" size="lg" onClick={() => setOpen(true)}>
            <FiPlus size={16} />
            {t("creatdeck")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-4 xl:grid-cols-3 md:!grid-cols-1 mt-5">
          {filteredDecks.map((item) => (
            <DeckCard
              key={item.deckId}
              deck={item}
              to={`/workshop/${item.deckId}`}
              showStatus
              showLanguage
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <CreateDeckModel
        open={open}
        setOpen={handleClose}
        fetchData={fetchData}
        editingDeck={editingDeck}
        setEditingDeck={setEditingDeck}
      />
      <CreateRegularDeckModel
        open={addRegularDeckModel}
        setIsOpen={setAddRegularDeckModel}
        onSuccess={() => {
          setOpen(false);
          fetchData();
        }}
        editMode={!!editingDeck}
        editingDeck={editingDeck}
        setEditingDeck={setEditingDeck}
      />
    </div>
  );
}
